/**
 * CheckboxComponent — это универсальный и гибкий компонент в пользовательском интерфейсе, который обеспечивает функциональность чекбокса. Он спроектирован таким образом, чтобы легко адаптироваться к различным задачам и контекстам в приложении.
 *
 * @link https://yt.devfast.io/youtrack/articles/NT-A-27/Komponent-Chekboks
 */

import type { AfterViewInit } from '@angular/core';
import { Component, DestroyRef, effect, inject, input, model, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { ControlValueAccessor } from '@angular/forms';
import { FormsModule, NgControl } from '@angular/forms';

import { tap } from 'rxjs';

@Component({
  imports: [FormsModule],
  selector: 'app-ui-checkbox',
  styleUrl: './styles/checkbox.component.scss',
  templateUrl: './checkbox.component.html',
})
export class CheckboxComponent implements ControlValueAccessor, AfterViewInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #uniqueId = `checkbox-${Math.random().toString(36).substring(2, 11)}`;
  readonly ngControl = inject(NgControl, { optional: true, self: true });

  /** Текстовая метка чекбокса */
  readonly label = input<string>();

  /** Устанавливает состояние загрузки компонента */
  readonly loading = input(false);

  /** Показывает или скрывает текстовую метку чекбокса */
  readonly showLabel = input<boolean>(true);

  /** Переключает тип иконки (галочка или плюс) */
  readonly iconType = input<'check' | 'plus'>('check');

  /** Устанавливает состояние компонента */
  readonly disabled = model(false);

  /** Устанавливает значение компонента */
  readonly value = model<boolean | null>();

  /** Событие клика по чекбоксу */
  readonly buttonClick = output<boolean>();

  /** Событие изменения состояния чекбокса */
  readonly checkboxChanged = output<boolean>();

  constructor() {
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
    this.#setupValueChangeEffect();
  }

  ngAfterViewInit(): void {
    if (!this.ngControl?.control) {
      return;
    }

    const control = this.ngControl.control;

    // Обрабатываем изменения значения из формы
    control.valueChanges
      .pipe(
        tap((value: boolean) => {
          // Выносим в untracked код, который не должен вызывать повторные вычисления
          this.value.set(value);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  /**
   * Возвращает уникальный идентификатор для чекбокса
   */
  get checkboxId(): string {
    return this.#uniqueId;
  }

  // Методы для ControlValueAccessor
  onChange: (value: boolean) => void = (): void => {
    return;
  };

  onTouched: () => void = (): void => {
    return;
  };

  changeCheckbox(value: boolean): void {
    this.value.set(value);
    this.onChange(value);
    this.checkboxChanged.emit(value);
  }

  /**
   * Переключает состояние чекбокса
   */
  toggleCheckmark(): void {
    // Если чекбокс неактивен или в состоянии загрузки, не меняем его состояние
    if (this.disabled() || this.loading()) {
      return;
    }

    if (this.ngControl?.control) {
      this.ngControl.control.patchValue(!this.ngControl.control.value);
    } else {
      this.value.update((value) => !value);
    }

    // Вызываем событие клика
    this.buttonClick.emit(!!this.value());
    this.onTouched();
  }

  // Реализация методов ControlValueAccessor
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(newDisabled: boolean): void {
    this.disabled.set(newDisabled);
  }

  writeValue(newValue: boolean): void {
    this.value.set(newValue);
  }

  /**
   * Отслеживает изменения значения и отправляет события
   */
  #setupValueChangeEffect(): void {
    effect(() => {
      const currentValue = this.value();
      if (currentValue === true || currentValue === false) {
        this.checkboxChanged.emit(currentValue);
      }
    });
  }
}
